import { Injectable } from "@angular/core";

@Injectable({
  providedIn: "root",
})
export class FireworksService {
  private fireworksCanvasGlobal: HTMLCanvasElement | null = null;
  private fireworksIntervalGlobal: any = null;
  private fireworksAnimationGlobal: any = null;
  private fireworksTimeoutGlobal: any = null;
  private resizeListenerGlobal: (() => void) | null = null;
  private isStartedGlobal = false;

  applyFireworks() {
    if (this.isStartedGlobal) return;
    this.isStartedGlobal = true;

    const canvas = document.createElement("canvas");
    canvas.style.position = "fixed";
    canvas.style.top = "0";
    canvas.style.left = "0";
    canvas.style.width = "100%";
    canvas.style.height = "100%";
    canvas.style.pointerEvents = "none";
    canvas.style.zIndex = "10";
    document.body.appendChild(canvas);

    this.fireworksCanvasGlobal = canvas;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      canvas.remove();
      this.fireworksCanvasGlobal = null;
      this.isStartedGlobal = false;
      return;
    }

    let canvasWidth = window.innerWidth;
    let canvasHeight = window.innerHeight;
    canvas.width = canvasWidth;
    canvas.height = canvasHeight;

    // Resize handler bound and stored for correct removal later
    this.resizeListenerGlobal = () => {
      canvasWidth = window.innerWidth;
      canvasHeight = window.innerHeight;
      canvas.width = canvasWidth;
      canvas.height = canvasHeight;
    };
    window.addEventListener("resize", this.resizeListenerGlobal);

    // Intense, saturated high-contrast colors (Neon Red, Bright White, Dodger Blue, Saturated Gold, Electric Orange, Violet Purple, Hot Pink, Bright Teal)
    const colorPalette = [
      "rgba(255, 0, 55, ",    // Intense Neon Red
      "rgba(255, 255, 255, ",  // Bright Pure White
      "rgba(0, 150, 255, ",   // Electric Cyan-Blue
      "rgba(255, 200, 0, ",   // Bright Golden Gold
      "rgba(255, 120, 0, ",   // Electric Orange
      "rgba(200, 0, 255, ",   // Neon Violet-Purple
      "rgba(255, 0, 180, ",   // Hot Pink
      "rgba(0, 255, 200, "    // Bright Mint Green/Teal
    ];

    interface Particle {
      x: number;
      y: number;
      vx: number;
      vy: number;
      alpha: number;
      decay: number;
      colorBase: string;
      size: number;
      isSparkle: boolean;
      history: { x: number; y: number }[];
    }

    interface Rocket {
      x: number;
      y: number;
      tx: number;
      ty: number;
      vx: number;
      vy: number;
      colorBase: string;
      trail: { x: number; y: number }[];
    }

    interface Star {
      x: number;
      y: number;
      size: number;
      alpha: number;
      speed: number;
      colorBase: string;
    }

    const rockets: Rocket[] = [];
    const particles: Particle[] = [];
    const bgStars: Star[] = [];

    // Initialize background stars with rich tones and slightly larger sizes for night sky atmosphere
    const maxStars = 40;
    const starColors = ["rgba(255, 255, 255, ", "rgba(255, 245, 200, ", "rgba(200, 235, 255, "];
    for (let i = 0; i < maxStars; i++) {
      bgStars.push({
        x: Math.random() * canvasWidth,
        y: Math.random() * (canvasHeight * 0.75),
        size: Math.random() * 1.8 + 0.6,
        alpha: Math.random() * 0.65 + 0.15,
        speed: 0.003 + Math.random() * 0.01,
        colorBase: starColors[Math.floor(Math.random() * starColors.length)]
      });
    }

    const createExplosion = (x: number, y: number, colorBase: string) => {
      // Increased particle count for richer visual impact (55-80 particles)
      const particleCount = 55 + Math.floor(Math.random() * 25);

      // Explosion types:
      // 0: single color
      // 1: duo-color (high contrast mix)
      // 2: multi-color rainbow burst
      const burstType = Math.random() < 0.28 ? 2 : (Math.random() < 0.48 ? 1 : 0);
      const secondaryColor = colorPalette[Math.floor(Math.random() * colorPalette.length)];

      for (let i = 0; i < particleCount; i++) {
        const angle = Math.random() * Math.PI * 2;
        // Wider explosion radius (increased speed vectors)
        const speed = (Math.random() * 6.0) + 1.5;
        const vx = Math.cos(angle) * speed;
        const vy = Math.sin(angle) * speed;

        let pColor = colorBase;
        if (burstType === 2) {
          pColor = colorPalette[Math.floor(Math.random() * colorPalette.length)];
        } else if (burstType === 1 && Math.random() > 0.5) {
          pColor = secondaryColor;
        }

        particles.push({
          x,
          y,
          vx,
          vy,
          alpha: 1.0,
          decay: 0.008 + Math.random() * 0.01, // longer decay for beautiful trails
          colorBase: pColor,
          size: Math.random() * 2.8 + 1.2, // significantly larger particles
          isSparkle: Math.random() > 0.5,
          history: []
        });
      }
    };

    const animate = () => {
      if (!ctx || !this.fireworksCanvasGlobal) return;
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);

      // 1. Draw twinkling background stars
      bgStars.forEach(star => {
        star.alpha += star.speed;
        if (star.alpha > 0.8 || star.alpha < 0.1) {
          star.speed = -star.speed;
        }
        ctx.beginPath();
        ctx.arc(star.x, star.y, star.size, 0, Math.PI * 2);
        ctx.fillStyle = star.colorBase + `${star.alpha})`;
        ctx.fill();
      });

      // 2. Rockets update and draw
      for (let i = rockets.length - 1; i >= 0; i--) {
        const r = rockets[i];
        r.trail.push({ x: r.x, y: r.y });
        if (r.trail.length > 8) r.trail.shift();

        r.x += r.vx;
        r.y += r.vy;
        r.vy += 0.04; // gravity/drag

        if (r.trail.length > 1) {
          ctx.beginPath();
          ctx.moveTo(r.trail[0].x, r.trail[0].y);
          for (let j = 1; j < r.trail.length; j++) {
            ctx.lineTo(r.trail[j].x, r.trail[j].y);
          }
          ctx.strokeStyle = r.colorBase + "0.45)";
          ctx.lineWidth = 2.5; // thicker rocket trail
          ctx.stroke();
        }

        // Explode if reaching apex
        if (r.vy >= -1.0 || r.y <= r.ty) {
          createExplosion(r.x, r.y, r.colorBase);
          rockets.splice(i, 1);
        }
      }

      // 3. Particles update and draw
      for (let i = particles.length - 1; i >= 0; i--) {
        const p = particles[i];
        p.history.push({ x: p.x, y: p.y });
        if (p.history.length > 5) p.history.shift();

        p.vx *= 0.98;
        p.vy *= 0.98;
        p.vy += 0.055; // gravity
        p.x += p.vx;
        p.y += p.vy;
        p.alpha -= p.decay;

        if (p.alpha <= 0) {
          particles.splice(i, 1);
          continue;
        }

        // Thicker and more visible trails
        if (p.history.length > 1) {
          ctx.beginPath();
          ctx.moveTo(p.history[0].x, p.history[0].y);
          for (let j = 1; j < p.history.length; j++) {
            ctx.lineTo(p.history[j].x, p.history[j].y);
          }
          ctx.strokeStyle = p.colorBase + `${p.alpha * 0.45})`;
          ctx.lineWidth = p.size * 1.3;
          ctx.stroke();
        }

        const drawAlpha = p.isSparkle && Math.random() > 0.45 ? p.alpha * 0.3 : p.alpha;

        // Secondary outer circle for a beautiful, performance-friendly GLOW effect
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 2.2, 0, Math.PI * 2);
        ctx.fillStyle = p.colorBase + `${drawAlpha * 0.18})`;
        ctx.fill();

        // Main core particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = p.colorBase + `${drawAlpha})`;
        ctx.fill();
      }

      // Cap active particles for performance stability
      while (particles.length > 300) {
        particles.shift();
      }

      this.fireworksAnimationGlobal = requestAnimationFrame(animate);
    };

    // Spawn rocket launcher loop - increased launch frequency (950ms) and more double launches
    this.fireworksIntervalGlobal = setInterval(() => {
      const launchCount = Math.random() < 0.4 ? 2 : 1;
      for (let l = 0; l < launchCount; l++) {
        const colorBase = colorPalette[Math.floor(Math.random() * colorPalette.length)];
        const startX = canvasWidth * 0.15 + Math.random() * (canvasWidth * 0.7);
        const startY = canvasHeight;
        const targetY = canvasHeight * 0.12 + Math.random() * (canvasHeight * 0.43);

        const vy = -(Math.sqrt(2 * 0.075 * (startY - targetY))) * 0.88;
        const vx = (Math.random() - 0.5) * 2.0;

        rockets.push({
          x: startX,
          y: startY,
          tx: startX + vx * 20,
          ty: targetY,
          vx,
          vy,
          colorBase,
          trail: []
        });
      }
    }, 950);

    animate();

    // Clear launcher loop slightly early (27.2 seconds) to let final rockets burst/fade naturally by 30 seconds
    const stopLauncherTimeout = setTimeout(() => {
      if (this.fireworksIntervalGlobal) {
        clearInterval(this.fireworksIntervalGlobal);
        this.fireworksIntervalGlobal = null;
      }
    }, 27200);

    // Auto-stop: Complete cleanup and canvas removal at exactly 30 seconds
    this.fireworksTimeoutGlobal = setTimeout(() => {
      clearTimeout(stopLauncherTimeout);
      this.stopFireworks();
    }, 30000);
  }

  stopFireworks() {
    if (this.fireworksIntervalGlobal) {
      clearInterval(this.fireworksIntervalGlobal);
      this.fireworksIntervalGlobal = null;
    }

    if (this.fireworksAnimationGlobal) {
      cancelAnimationFrame(this.fireworksAnimationGlobal);
      this.fireworksAnimationGlobal = null;
    }

    if (this.fireworksTimeoutGlobal) {
      clearTimeout(this.fireworksTimeoutGlobal);
      this.fireworksTimeoutGlobal = null;
    }

    if (this.resizeListenerGlobal) {
      window.removeEventListener("resize", this.resizeListenerGlobal);
      this.resizeListenerGlobal = null;
    }

    if (this.fireworksCanvasGlobal) {
      if (this.fireworksCanvasGlobal.parentNode) {
        this.fireworksCanvasGlobal.parentNode.removeChild(this.fireworksCanvasGlobal);
      }
      this.fireworksCanvasGlobal = null;
    }

    this.isStartedGlobal = false;
  }
}
